import React from 'react';
import { sentryUtils } from '@/utils/sentry';
import SurveyErrorFallback from './SurveyErrorFallback';

interface SurveyErrorBoundaryProps {
  children: React.ReactNode;
  surveyId?: string;
  surveyName?: string;
}

interface SurveyErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class SurveyErrorBoundary extends React.Component<SurveyErrorBoundaryProps, SurveyErrorBoundaryState> {
  constructor(props: SurveyErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): SurveyErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Set survey context for better error tracking
    if (this.props.surveyId) {
      sentryUtils.setTag('surveyId', this.props.surveyId);
    }
    if (this.props.surveyName) {
      sentryUtils.setTag('surveyName', this.props.surveyName);
    }

    // Set feature context
    sentryUtils.setContext('survey-error', {
      surveyId: this.props.surveyId,
      surveyName: this.props.surveyName,
      componentStack: errorInfo.componentStack,
      feature: 'survey',
    });

    // Capture the error with Sentry
    sentryUtils.captureException(error, {
      tags: {
        feature: 'survey',
        component: 'SurveyErrorBoundary',
        ...(this.props.surveyId && { surveyId: this.props.surveyId }),
      },
      extra: {
        errorInfo,
        surveyId: this.props.surveyId,
        surveyName: this.props.surveyName,
      },
    });

    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return <SurveyErrorFallback 
        error={this.state.error} 
        surveyId={this.props.surveyId}
        surveyName={this.props.surveyName}
        onReset={() => this.setState({ hasError: false, error: undefined, errorInfo: undefined })}
      />;
    }

    return this.props.children;
  }
}


// HOC to wrap components with survey error boundary
export const withSurveyErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  surveyId?: string,
  surveyName?: string
) => {
  const WrappedComponent = (props: P) => (
    <SurveyErrorBoundary surveyId={surveyId} surveyName={surveyName}>
      <Component {...props} />
    </SurveyErrorBoundary>
  );

  WrappedComponent.displayName = `withSurveyErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
}; 